import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from '@angular/router';
import { CoreService, DialogResult, DialogService, DspxCardSetBaseFormComponent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { FILTER_QUESTION_CODES, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, SECTION_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { Mapper, MapperBuilder, TRANSFORM } from '../../helpers/mapper';
import { CareAllowancePlacementInvoiceGridFilterRequest } from '../../interfaces/care-allowance-placement-invoice-grid-filter-request.interface';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-care-allowance-placement-invoice-list',
  templateUrl: './care-allowance-placement-invoice-list.component.html',
  styleUrl: './care-allowance-placement-invoice-list.component.scss'
})
export class CareAllowancePlacementInvoiceListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {
  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  filterQuestionToFilterModelMapper: Mapper;
  activeDialog: string = '';
  private routePrefix = '';
  private placementId = '';
  private isPlacementGrid = !!this.placementId;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService,
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.careAllowancePlacementInvoiceList,
      () =>{
        const paramMap = this.route.snapshot.paramMap;
        const placementId = paramMap.get('placementId');
        return placementId 
        ? API_ENDPOINTS.dashboard.getCareAllowancePlacementInvoicesByApplication
        : API_ENDPOINTS.dashboard.getCareAllowancePlacementInvoices},
      false);

    this.filterQuestionToFilterModelMapper =
      new MapperBuilder()
        .WithDefaultTargetFieldMapping(/Filter$/i, "") // When target field name is not specified, just use the source field's name, but drop "Filter" from the end
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.oracleVendorNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.carerContactIdFilter)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.carerFirstNameFilter)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.carerSurnameFilter)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.carerDobFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.invoiceNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.invoiceDateFromFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.invoiceDateToFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.paymentNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.paymentDateFromFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.careAllowancePlacementInvoiceListForm.paymentDateToFilter, TRANSFORM.STRING_TO_DATE)
        .BuildMapper();
  }

  ngOnInit(): void {
    // We get the first part of the URL so that we can navigate back to that route after the Cancel or Save button is clicked.
    let index = this.router.url.indexOf(`/${ROUTE_CONSTANTS.careAllowancePlacementInvoice.listPrefix}`);
    this.routePrefix = this.router.url.substring(0, index);

    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const placementId = paramMap.get('placementId');
        if (placementId) {
          this.placementId = placementId;
        }
        this.isPlacementGrid =  !!this.placementId;
      })
    );

    this.coreService.conditionFunctions = 
    {
      showInvoiceSearchFilter: () => !this.isPlacementGrid,
      showInvoiceByApplicationFilter: () => this.isPlacementGrid,
      showPlacementDetail: () => this.isPlacementGrid,
      showBackButton: () => this.isPlacementGrid,
      showInvoiceGrid: () => !this.isPlacementGrid,
      showInvoiceGridForPlacement: () =>  this.isPlacementGrid,
    };

    this.coreService.applicationFunctions = {
      exit: () => this.navigateBack(),
      showBackButton: () => this.isPlacementGrid,
    };
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoad(event: any): void {
    this.loadGridDataOnFormLoad(this.getInvoiceFilterRequest());
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.careAllowancePlacementInvoiceListForm.invoiceSearchGridFilter:
      case QUESTION_CODES.careAllowancePlacementInvoiceListForm.invoiceGridFilter:
        {
          const filterRequest = this.getInvoiceFilterRequest();
          if (filterRequest.invoiceDateFrom 
            && filterRequest.invoiceDateTo 
            && filterRequest.invoiceDateFrom > filterRequest.invoiceDateTo) 
            {
              this.dialogService.showDialog(
                POP_UP_CODES.careAllowancePlacementInvoiceList.invalidDateRange)  
              return;
            }
          this.loadGridData(filterRequest);
          break;
        }
    }
  }

  actionButton_invoiceGrid_datagrid_cancelInvoice_Clicked(event: any) {
    this.activeDialog = POP_UP_CODES.careAllowancePlacementInvoiceList.cancelInvoice;
    this.dialogService.showDialog(this.activeDialog, event.event.data);
  }

  actionButton_invoiceSearchGrid_datagrid_colLink_eppPlacementId_Clicked(event: any): void {
    const navExtras = this.getNavigationExtras();
    this.router.navigate(['/', ROUTE_CONSTANTS.careAllowancePlacement.list, ROUTE_CONSTANTS.careAllowancePlacement.prefix, event.event.data.placementId], navExtras);
  }

  dialogButton_cancelInvoice_Clicked(event: any) {
    this.activeDialog = POP_UP_CODES.careAllowancePlacementInvoiceList.confirmCancelInvoice;
    this.registerSubscription(
      this.dialogService
        .showConfirm(this.activeDialog)
        .subscribe((response) => {
          if (response === DialogResult.ACCEPTED) {
            this.spinnerService.showSpinner();
            this.appDataService
              .post(API_ENDPOINTS.careAllowancePlacement.cancelInvoice, { invoiceId: event.id, cancellationReason: event.cancellationReason })
              .pipe(
                take(1),
                this.errorService.errorHandler(),
                finalize(() => this.spinnerService.hideSpinner())
              )
              .subscribe((data) => {
                this.loadGridDataOnFormLoad(this.getInvoiceFilterRequest());
              });
          }
        })
    );
  }

  private getInvoiceFilterRequest(): CareAllowancePlacementInvoiceGridFilterRequest {

    let filter =  this.isPlacementGrid
      ? QUESTION_CODES.careAllowancePlacementInvoiceListForm.invoiceGridFilter
      : QUESTION_CODES.careAllowancePlacementInvoiceListForm.invoiceSearchGridFilter;
    const filterModel = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.careAllowancePlacementInvoiceListForm.invoices,
      PAGE_NUMBERS.careAllowancePlacementInvoiceListForm.invoices,
      SECTION_CODES.careAllowancePlacementInvoiceListForm.invoiceSection,
      filter
    );

    let filterRequest: CareAllowancePlacementInvoiceGridFilterRequest = this.filterQuestionToFilterModelMapper.Map(filterModel);

    if (filterRequest == null) {
      filterRequest = { placementId: this.placementId };
    }
    else {
      filterRequest.placementId = this.placementId;
    }

    return filterRequest;
  }

  private navigateBack() {
    const navExtras = this.getNavigationExtras();
    this.router.navigate([this.routePrefix], navExtras);
  }

  private getNavigationExtras(): NavigationExtras {
    return {
      state: {
        placementFilterRequest: history.state.placementFilterRequest,
        personFilterRequest: history.state.personFilterRequest,
      }
    };
  }
}
